// -----------------------------------------------------------------------------
// Desktop notifications primitive.
//
// Wraps the browser Notification API in a small React-friendly shape:
//   - tracks the current permission ("default" / "granted" / "denied")
//   - tracks a per-user "enabled" preference persisted to localStorage so
//     the user can opt in/out without re-prompting the browser
//   - exposes `request()` (must be called from a user gesture) and
//     `show(...)` for posting notifications when both `permission` is
//     "granted" and the user has opted in.
//
// Domain-specific wiring (e.g. chat messages -> notifications) lives in
// `useChatDesktopNotifications`; this hook stays generic so any future
// notification source can reuse it.
// -----------------------------------------------------------------------------

import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "chat.desktopNotifications.enabled";

export type DesktopPermission = "default" | "granted" | "denied" | "unsupported";

export interface DesktopNotificationOptions {
  title: string;
  body?: string;
  icon?: string;
  /** Notifications sharing the same tag replace each other rather than
   *  stacking - useful for "10 messages in #general" style coalescing. */
  tag?: string;
  onClick?: () => void;
}

export interface UseDesktopNotifications {
  supported: boolean;
  permission: DesktopPermission;
  enabled: boolean;
  /** True when notifications can actually be shown (supported + granted + opted in). */
  canShow: boolean;
  /** Prompts the browser for permission. Must be called from a user gesture. */
  request: () => Promise<DesktopPermission>;
  setEnabled: (enabled: boolean) => void;
  show: (options: DesktopNotificationOptions) => Notification | null;
}

function readStoredEnabled(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

function readBrowserPermission(): DesktopPermission {
  if (typeof window === "undefined" || !("Notification" in window)) {
    return "unsupported";
  }
  return window.Notification.permission as DesktopPermission;
}

export default function useDesktopNotifications(): UseDesktopNotifications {
  const supported =
    typeof window !== "undefined" && "Notification" in window;

  const [permission, setPermission] = useState<DesktopPermission>(
    readBrowserPermission
  );
  const [enabled, setEnabledState] = useState<boolean>(readStoredEnabled);

  // If the user revoked permission elsewhere (browser settings) while
  // the tab was open we want to reflect that the next time the tab
  // regains focus.
  useEffect(() => {
    if (!supported) return;
    const refresh = () => setPermission(readBrowserPermission());
    document.addEventListener("visibilitychange", refresh);
    window.addEventListener("focus", refresh);
    return () => {
      document.removeEventListener("visibilitychange", refresh);
      window.removeEventListener("focus", refresh);
    };
  }, [supported]);

  const setEnabled = useCallback((next: boolean) => {
    setEnabledState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next ? "true" : "false");
    } catch {
      /* storage disabled - fine, in-memory is enough for the session */
    }
  }, []);

  const request = useCallback(async (): Promise<DesktopPermission> => {
    if (!supported) return "unsupported";
    try {
      const result = (await window.Notification.requestPermission()) as DesktopPermission;
      setPermission(result);
      // First-time grant: opt the user in automatically. They can flick
      // the toggle off later if they change their mind.
      if (result === "granted") setEnabled(true);
      return result;
    } catch {
      return permission;
    }
  }, [supported, permission, setEnabled]);

  const canShow =
    supported && permission === "granted" && enabled;

  const show = useCallback(
    (options: DesktopNotificationOptions): Notification | null => {
      if (!canShow) return null;
      try {
        const notif = new window.Notification(options.title, {
          body: options.body,
          icon: options.icon,
          tag: options.tag,
        });
        if (options.onClick) {
          notif.onclick = () => {
            window.focus();
            try {
              options.onClick?.();
            } finally {
              notif.close();
            }
          };
        }
        return notif;
      } catch {
        return null;
      }
    },
    [canShow]
  );

  return {
    supported,
    permission,
    enabled,
    canShow,
    request,
    setEnabled,
    show,
  };
}
